import { c as _c } from "react/compiler-runtime";
import BloombergAnywhereLogo from '@/components/Atoms/BloombergAnywhereLogo/BloombergAnywhereLogo';
import BnefLogo from '@/components/Atoms/BnefLogo/BnefLogo';
import { P } from '@/components/Atoms/P/P';
import { H1 } from '@/components/Core/Heading';
import { Stack } from '@/components/Molecules/Stack/Stack';
import { CHINA_ICP_CODE } from '@/components/Organisms/Footer/Footer';
import { DebugInfo } from '@/components/Pages/Errors/DebugInfo';
import { LoginErrorMessage } from '@/components/Pages/Login/LoginErrorMessage';
import { getClientRuntimeConfig } from '@/config/runtime/clientRuntimeConfig';
import { useEffectDeps } from '@/hooks/useEffectDeps';
import { useSkipFirstCall } from '@/hooks/useSkipFirstCall';
import { prepareBBAnalytics, refreshBBAnalyticsProviders, trackPageLoad } from '@/services/Analytics/BBAnalytics';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import type { IBaseAnalyticsData } from '@/services/Analytics/bnefAnalyticsInterfaces';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import SessionStorage from '@/services/SessionStorage';
import classNames from '@/utils/classNames';
import Link from 'next/link';
import type { FC, FormHTMLAttributes, HTMLAttributes } from 'react';
import { useEffect } from 'react';
import type { FCh } from 'react-fc';
import styles from './LoginTemplate.module.css';
interface IBackground {
  url: string;
  color: string;
  preloaded?: boolean;
}
const allBackgrounds: IBackground[] = [{
  color: '#887d81',
  url: 'https://assets.bwbx.io/images/users/iIyChnKQ1lHc/i3O54QrAM2fY/v1/-1x-1.webp'
}, {
  color: '#2e2f2f',
  url: 'https://assets.bwbx.io/images/users/iIyChnKQ1lHc/i8DgsT2lekYY/v1/-1x-1.webp'
}, {
  color: '#889fb3',
  url: 'https://assets.bwbx.io/images/users/iIyChnKQ1lHc/idBugBrseqvQ/v1/-1x-1.webp'
}, {
  color: '#0d0c0d',
  url: 'https://assets.bwbx.io/images/users/iIyChnKQ1lHc/iNVhAgLqXcWw/v1/-1x-1.webp'
}, {
  color: '#808791',
  url: 'https://assets.bwbx.io/images/users/iIyChnKQ1lHc/itkqobte671U/v1/-1x-1.webp'
}, {
  color: '#b38a5b',
  url: 'https://assets.bwbx.io/images/users/iIyChnKQ1lHc/iSlg.uaLCXSs/v1/-1x-1.webp'
}, {
  color: '#808286',
  url: 'https://assets.bwbx.io/images/users/iIyChnKQ1lHc/ih.wZ8eN0tCI/v1/-1x-1.webp'
}, {
  color: '#5e797b',
  url: 'https://assets.bwbx.io/images/users/iIyChnKQ1lHc/iCb95HHLZ0SU/v1/-1x-1.webp'
}, {
  color: '#343c4b',
  url: 'https://assets.bwbx.io/images/users/iIyChnKQ1lHc/iuwRLHMnxeDY/v1/-1x-1.webp'
}];
const BACKGROUND_SELECTION_KEY = ['login', 'backgroundImage'];
export const LoginTemplate: FCh<IProps> = t0 => {
  const $ = _c(33);
  const {
    title,
    children,
    error,
    hideDebugPanel,
    wide,
    analyticsMetadata: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const analyticsMetadata = t2;
  const skipFirstRender = useSkipFirstCall();
  let t3;
  if ($[2] !== analyticsMetadata) {
    t3 = () => {
      prepareBBAnalytics(getClientRuntimeConfig().services.clientAnalytics, analyticsMetadata);
      trackPageLoad();
    };
    $[2] = analyticsMetadata;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== analyticsMetadata || $[6] !== skipFirstRender) {
    t5 = () => skipFirstRender(() => {
      refreshBBAnalyticsProviders(analyticsMetadata);
    });
    $[5] = analyticsMetadata;
    $[6] = skipFirstRender;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== analyticsMetadata) {
    t6 = [analyticsMetadata];
    $[8] = analyticsMetadata;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffectDeps(t5, t6);
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  useEffect(_temp, t7);
  const t8 = wide ? "720px" : "386px";
  let t9;
  if ($[11] !== t8) {
    t9 = {
      "--_LoginPageWidth": t8
    };
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = t9 as React.CSSProperties;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className={styles.logo}><BnefLogo /></div>;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== error) {
    t12 = <div className={styles.errorMessageArea}><LoginErrorMessage errorKey={error} /></div>;
    $[14] = error;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== title) {
    t13 = <H1>{title}</H1>;
    $[16] = title;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <BloombergAnywhereLogo />;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className={styles.bba}>{t14}<P size="standard">Run{" "}<a href="bbg://screens/NEFW" target="_blank" rel="noopener noreferrer" data-analytics-tracking={buildTrackingObject(TrackingAction.FollowLink, TrackingCategory.General, "Login: NEFW")}>{"NEFW<GO>"}</a>{" "}on your Terminal or contact your sales representative.</P></div>;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Link href="/login/contact" data-analytics-tracking={buildTrackingObject(TrackingAction.FollowLink, TrackingCategory.ContactForm, "Login: Contact us")}>Contact Us</Link>;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className={styles.panelFooter}>{t15}<P size="small" className={styles.footerExtras}>{t16}<a href="http://beian.miit.gov.cn/" rel="noopener noreferrer" data-analytics-tracking={buildTrackingObject(TrackingAction.FollowLink, TrackingCategory.General, "Login: ICP License")}>{CHINA_ICP_CODE}</a></P></div>;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== children || $[23] !== t13) {
    t18 = <div className={styles.panel}>{t13}{children}{t17}</div>;
    $[22] = children;
    $[23] = t13;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== error || $[26] !== hideDebugPanel) {
    t19 = error && !hideDebugPanel && <DebugInfo className={styles.debugInfo} />;
    $[25] = error;
    $[26] = hideDebugPanel;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t10 || $[29] !== t12 || $[30] !== t18 || $[31] !== t19) {
    t20 = <div className={styles.loginLayout} data-sentry-mask={true} data-cds-theme="dark" style={t10}>{t11}{t12}{t18}{t19}</div>;
    $[28] = t10;
    $[29] = t12;
    $[30] = t18;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  return t20;
};
export const ButtonRow: FC<HTMLAttributes<HTMLDivElement>> = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = classNames(styles.buttonRow, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const LoginForm: FC<FormHTMLAttributes<HTMLFormElement>> = t0 => {
  const $ = _c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <Stack as="form" method="post" $gap="var(--size-grid-double)" {...props}>{children}</Stack>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
interface IProps {
  title: string;
  error?: string;
  hideDebugPanel?: boolean;
  wide?: boolean;
  analyticsMetadata?: IBaseAnalyticsData;
}
function _temp() {
  let chosenBg = SessionStorage.get(BACKGROUND_SELECTION_KEY);
  if (!chosenBg) {
    chosenBg = allBackgrounds[Math.floor(Math.random() * allBackgrounds.length)];
    SessionStorage.set(BACKGROUND_SELECTION_KEY, {
      ...chosenBg,
      preloaded: true
    });
  }
  document.body.style.setProperty("--LoginBackground", `${chosenBg.color} url(${chosenBg.url})`);
}